import { useEffect, useState } from 'react';
import axios from 'axios';
import { Warning, Sparkle, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Anomalies() {
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [explaining, setExplaining] = useState(null);
  const [explanation, setExplanation] = useState(null);

  useEffect(() => { loadAnomalies(); }, []);

  const loadAnomalies = async () => {
    try {
      const { data } = await axios.get(`${API}/anomalies`, { withCredentials: true });
      setAnomalies(data);
    } catch (error) {
      toast.error('Failed to load anomalies');
    } finally {
      setLoading(false);
    }
  };

  const handleExplain = async (anomaly) => {
    setExplaining(anomaly.id);
    setExplanation(null);
    try {
      const { data } = await axios.post(`${API}/ai/explain-anomaly`,
        { anomaly_type: anomaly.type, description: anomaly.description },
        { withCredentials: true }
      );
      setExplanation({ id: anomaly.id, text: data.explanation });
    } catch (e) {
      toast.error('AI explanation failed');
    } finally {
      setExplaining(null);
    }
  };

  const severityColors = {
    high: 'bg-rose-50/80 border-rose-300 text-rose-900',
    medium: 'bg-amber-50/80 border-amber-300 text-amber-900',
    low: 'bg-blue-50/80 border-blue-300 text-blue-900'
  };
  const severityIconColor = {
    high: 'text-rose-600',
    medium: 'text-amber-600',
    low: 'text-blue-600'
  };

  if (loading) return <div className="p-8">Loading...</div>;

  const grouped = anomalies.reduce((acc, a) => {
    acc[a.severity] = (acc[a.severity] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-8" data-testid="anomalies-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight ocean-text" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="anomalies-title">Anomaly Detection</h1>
        <p className="mt-2 text-slate-600">Review and resolve financial anomalies</p>
      </div>

      <div className="grid grid-cols-3 gap-5 mb-8">
        <div className="glass-card p-5 border-l-4 border-l-rose-500" data-testid="high-severity-count">
          <p className="text-xs font-bold uppercase tracking-widest text-rose-600">High Severity</p>
          <p className="text-4xl font-bold text-rose-600 mt-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>{grouped.high || 0}</p>
        </div>
        <div className="glass-card p-5 border-l-4 border-l-amber-500" data-testid="medium-severity-count">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-600">Medium Severity</p>
          <p className="text-4xl font-bold text-amber-600 mt-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>{grouped.medium || 0}</p>
        </div>
        <div className="glass-card p-5 border-l-4 border-l-blue-500" data-testid="low-severity-count">
          <p className="text-xs font-bold uppercase tracking-widest text-blue-600">Low Severity</p>
          <p className="text-4xl font-bold text-blue-600 mt-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>{grouped.low || 0}</p>
        </div>
      </div>

      <div className="glass-card">
        <div className="p-6 border-b border-slate-200/60">
          <h2 className="text-xl font-bold" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Detected Anomalies</h2>
        </div>
        <div className="divide-y divide-slate-200/60" data-testid="anomalies-list">
          {anomalies.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="no-anomalies">
              <Sparkle size={48} className="mx-auto mb-3 text-blue-400 opacity-50" weight="fill" />
              <p className="font-semibold">All clear!</p>
              <p className="text-sm">No anomalies detected. Your books are in good shape.</p>
            </div>
          ) : anomalies.map((a) => (
            <div key={a.id} className={`p-4 ${severityColors[a.severity]} border-l-4`} data-testid={`anomaly-${a.id}`}>
              <div className="flex items-start gap-3">
                <Warning size={20} className={severityIconColor[a.severity]} weight="fill" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold uppercase tracking-widest">{a.type.replace(/_/g, ' ')}</span>
                    <span className={`text-xs px-2 py-0.5 rounded border ${a.severity === 'high' ? 'border-rose-300' : a.severity === 'medium' ? 'border-amber-300' : 'border-blue-300'}`}>
                      {a.severity}
                    </span>
                  </div>
                  <p className="text-sm">{a.description}</p>
                  <p className="text-xs text-slate-500 mt-1">Detected: {new Date(a.detected_at).toLocaleString()}</p>
                  
                  {explanation && explanation.id === a.id && (
                    <div className="mt-3 p-3 bg-white/70 rounded-lg border border-blue-200" data-testid={`explanation-${a.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-blue-700">
                          <Sparkle size={12} weight="fill" /> AI Explanation
                        </div>
                        <button onClick={() => setExplanation(null)} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
                      </div>
                      <p className="text-sm text-slate-800 leading-relaxed">{explanation.text}</p>
                    </div>
                  )}
                </div>
                <Button
                  onClick={() => handleExplain(a)}
                  disabled={explaining === a.id}
                  size="sm"
                  variant="outline"
                  className="bg-white/60 gap-1 text-xs"
                  data-testid={`explain-${a.id}`}
                >
                  <Sparkle size={14} weight="fill" /> {explaining === a.id ? '...' : 'Explain'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
